import { Bundle, BundleAccess, Prisma } from '@prisma/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import {
  BundleAccessEligibilityAggregate,
  BundleAccessWithChildrenResult,
  BundleWithChildren,
  CreateBundleDto,
  ValidateAccessEligibilityResult,
  ValidateEligibilityParams,
} from '@app/domain-bundle-interfaces';
import { MutateResult } from '@app/domain-interfaces';
import { CommonFindResult } from '@app/infra-interfaces';
import { Axios } from '@app/web-components';

const BUNDLE_ALL_QUERY_KEY = 'BUNDLE_ALL_QUERY_KEY';

export const bundleKeys = {
  all: [BUNDLE_ALL_QUERY_KEY] as const,
  lists: () => [...bundleKeys.all, 'list'] as const,
  list: (filters: object) => [...bundleKeys.lists(), filters] as const,
  items: () => [...bundleKeys.all, 'items'] as const,
  item: (bundleId: number) => [...bundleKeys.items(), bundleId] as const,
};

/** use when unable to use the useFindBundleMany hook */
export const useFindBundleFetch = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  const findBundle = <T>(params: Prisma.BundleFindManyArgs) => {
    return queryClient.fetchQuery<CommonFindResult<T>>({
      queryKey: bundleKeys.list(params),
      queryFn: async () => {
        const { data } = await axios.post('/bundles/find', params);
        return data;
      },
    });
  };

  return findBundle;
};

export const useFindBundle = <T>(params: Prisma.BundleFindManyArgs) => {
  const axios = Axios.useAxiosApi();

  return useQuery<CommonFindResult<T>>({
    queryKey: bundleKeys.list(params),
    queryFn: async () => {
      const { data } = await axios.post('/bundles/find', params);
      return data;
    },
  });
};

/** use when unable to use the useFindBundle hook */
export const useFindBundleByIdFetch = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  const findBundleById = (bundleId: number) => {
    return queryClient.fetchQuery<BundleWithChildren>({
      queryKey: bundleKeys.item(bundleId),
      queryFn: async () => {
        const { data } = await axios.get(`/bundles/${bundleId}`);
        return data;
      },
    });
  };

  return findBundleById;
};

export const useFindBundleById = ({ bundleId, enabled = true }: { bundleId?: number | null; enabled?: boolean }) => {
  const axios = Axios.useAxiosApi();

  return useQuery<BundleWithChildren, AxiosError>({
    queryKey: bundleKeys.item(bundleId ?? 0),
    queryFn: async () => {
      const { data } = await axios.get(`/bundles/${bundleId}`);
      return data;
    },
    enabled: !!bundleId && enabled,
  });
};

export const useGetEligibilityCounts = ({
  bundleId,
  enabled = true,
}: {
  bundleId?: number | null;
  enabled?: boolean;
}) => {
  const axios = Axios.useAxiosApi();

  return useQuery<BundleAccessEligibilityAggregate[], AxiosError>({
    queryKey: bundleKeys.list({ bundleId, eligibilityCounts: true }),
    queryFn: async () => {
      const { data } = await axios.get(`/bundles/eligibility-counts/${bundleId}`);
      return data;
    },
    enabled: !!bundleId && enabled,
  });
};

export const useValidateAccessEligibility = ({
  enabled = true,
  ...params
}: ValidateEligibilityParams & {
  enabled?: boolean;
}) => {
  const axios = Axios.useAxiosApi();

  return useQuery<ValidateAccessEligibilityResult[]>({
    queryKey: bundleKeys.list({ params }),
    queryFn: async () => {
      const { data } = await axios.post('/bundles/eligibility', params);
      return data;
    },
    enabled: !!params.bundleId && !!params.requesterEnterpriseId && (enabled ?? true),
  });
};

export const useFindBundleAccessByBundleId = ({
  bundleId,
  enabled = true,
}: {
  bundleId?: number | null;
  enabled?: boolean;
}) => {
  const axios = Axios.useAxiosApi();
  return useQuery<BundleAccessWithChildrenResult[], AxiosError>({
    queryKey: bundleKeys.list({ bundleId, access: true }),
    queryFn: async () => {
      const { data } = await axios.get(`/bundles/bundle-access/${bundleId}`);
      return data;
    },
    enabled: !!bundleId && enabled,
  });
};

export const useCreateBundle = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateBundleDto) => {
      return axios.post<MutateResult<BundleWithChildren>>('/bundles', dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BUNDLE_ALL_QUERY_KEY] });
    },
  });
};

export const useEditBundle = (id: number) => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateBundleDto) => {
      return axios.put<MutateResult<BundleWithChildren>>(`/bundles/${id}`, dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BUNDLE_ALL_QUERY_KEY] });
    },
  });
};

export const usePatchBundle = (id: number | null | undefined) => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: Partial<CreateBundleDto>) => {
      return axios.patch<MutateResult<BundleWithChildren>>(`/bundles/${id}`, dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BUNDLE_ALL_QUERY_KEY] });
    },
  });
};

export const useDeleteBundle = ({ validateOnly = false }: { validateOnly?: boolean }) => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => {
      return axios.delete<MutateResult<Bundle>>(`/bundles/${id}?validateonly=${validateOnly}`);
    },
    onSuccess: () => {
      if (!validateOnly) {
        queryClient.invalidateQueries({ queryKey: [BUNDLE_ALL_QUERY_KEY] });
      }
    },
  });
};

export const usePatchBundleAccess = (bundleId: number) => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ accessId, isEagerAnchor }: { accessId: number; isEagerAnchor?: boolean }) => {
      return axios.patch<BundleAccess>(`/bundles/${bundleId}/access/${accessId}`, { isEagerAnchor });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BUNDLE_ALL_QUERY_KEY] });
    },
  });
};
